import React from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TextField } from 'edenred-ui';

import {
  createRules,
  FormFields,
  useAsyncValidator,
  useAsyncValidatorController
} from '@epi-forms/helpers';
import { isUsernameAvailableRequest } from '@epi-api/username';
import * as Validators from '@epi-helpers/formHelpers/validators';
import { RecaptchaError } from '@epi-helpers/ReCaptchaHelper';
import { UsernameFieldProps } from '@epi-models/forms/Controls';

const USERNAME_TAKEN_ERROR = 'Username is not available';

function toUsernameAvailabilityError(error: unknown) {
  if (error instanceof Error && error.message === USERNAME_TAKEN_ERROR) {
    return error;
  }

  if (error instanceof RecaptchaError) {
    return error;
  }

  return Object.assign(new Error('Username availability check failed'), {
    translationKey: 'validation_messages.cannot_check_username'
  });
}

export function UsernameField({
  validators,
  validateAvailability = true,
  label = 'controls.username_label'
}: UsernameFieldProps) {
  const name = FormFields.Username;
  const { control } = useFormContext();
  const { t } = useTranslation();
  const defaultValidators = [
    Validators.required,
    Validators.minLength(6),
    Validators.maxLength(60),
    Validators.username,
    Validators.noWhitespaces,
    Validators.emailForbidden
  ];
  const rules = createRules(validators || defaultValidators);

  const {
    field: { ref, value, onChange: setValue },
    fieldState: { error }
  } = useController({
    name,
    control,
    rules
  });

  const usernameAvailabilityValidator = async () => {
    try {
      const valid = await isUsernameAvailableRequest(value);

      if (!valid) {
        throw new Error(USERNAME_TAKEN_ERROR);
      }
    } catch (error) {
      throw toUsernameAvailabilityError(error);
    }
  };

  const validateUsername = useAsyncValidator({
    fieldName: name,
    validator: usernameAvailabilityValidator,
    validatorName: FormFields.Username
  });

  const {
    isError,
    isInfo,
    helperText,
    inputProps,
    setAsyncUnverified,
    clearAsyncUnverified
  } = useAsyncValidatorController({
    value,
    error,
    name,
    invalidMsg: t('validation_messages.username_taken'),
    processingMsg: t('validation_messages.username_availability_processing')
  });

  const onChange = event => {
    if (validateAvailability) {
      setAsyncUnverified();
    }
    setValue(event);
  };

  const onBlur = async () => {
    if (validateAvailability) {
      await validateUsername();
      clearAsyncUnverified();
    }
  };

  return (
    <TextField
      label={t(label)}
      required
      value={value}
      id={name}
      error={isError}
      info={isInfo}
      InputProps={validateAvailability ? inputProps : undefined}
      helperText={helperText || <>&nbsp;</>}
      inputRef={ref}
      onBlur={onBlur}
      onChange={onChange}
    />
  );
}
